// Data-driven instrument registry (docs/05 §5.6). Adding an instrument = one
// entry here + one factory. The palette UI and the looper read from this.

import { createDrumKit } from "./drumKit.ts";
import {
  createBells,
  createElectroKit,
  createFmBass,
  createGrandPiano,
  createHarp,
  createKalimba,
  createMarimba,
  createOrgan,
  createShaker,
  createStrings,
  createSynthBrass,
  createSynthLead,
} from "./more.ts";
import { createPad } from "./pad.ts";
import { createSynthBass } from "./synthBass.ts";
import { createSynthKeys } from "./synthKeys.ts";
import type { Instrument, InstrumentCategory, PlayArchetype } from "./types.ts";

export type { Instrument, InstrumentCategory, PlayArchetype } from "./types.ts";

export interface InstrumentDef {
  id: string;
  name: string;
  category: InstrumentCategory;
  archetype: PlayArchetype;
  scaleLocked: boolean;
  polyphony: "mono" | "poly" | number;
  color: string; // lane color
  playHint: string; // keyboard mapping shown in the UI
  create(): Instrument;
}

export const INSTRUMENTS: readonly InstrumentDef[] = [
  {
    id: "drum-kit",
    name: "Drum kit",
    category: "drums",
    archetype: "air-drum",
    scaleLocked: false,
    polyphony: 6,
    color: "#ff8f3f",
    playHint: "Z X C V B N",
    create: createDrumKit,
  },
  {
    id: "synth-bass",
    name: "Synth bass",
    category: "bass",
    archetype: "theremin",
    scaleLocked: true,
    polyphony: "mono",
    color: "#6ea8fe",
    playHint: "A S D F G H J K",
    create: createSynthBass,
  },
  {
    id: "synth-keys",
    name: "Synth keys",
    category: "keys",
    archetype: "air-keys",
    scaleLocked: true,
    polyphony: "poly",
    color: "#5eead4",
    playHint: "A S D F G H J K",
    create: createSynthKeys,
  },
  {
    id: "warm-pad",
    name: "Warm pad",
    category: "pads",
    archetype: "theremin",
    scaleLocked: true,
    polyphony: "poly",
    color: "#c08cff",
    playHint: "A S D F G H J K",
    create: createPad,
  },
  { id: "electro-kit", name: "808 kit", category: "drums", archetype: "air-drum", scaleLocked: false, polyphony: 6, color: "#ff6b6b", playHint: "Z X C V B N", create: createElectroKit },
  { id: "grand-piano", name: "Grand piano", category: "keys", archetype: "air-keys", scaleLocked: true, polyphony: "poly", color: "#e8e8ea", playHint: "A S D F G H J K", create: createGrandPiano },
  { id: "organ", name: "Organ", category: "keys", archetype: "air-keys", scaleLocked: true, polyphony: "poly", color: "#66d9c2", playHint: "A S D F G H J K", create: createOrgan },
  { id: "marimba", name: "Marimba", category: "keys", archetype: "air-keys", scaleLocked: true, polyphony: "poly", color: "#e6b450", playHint: "A S D F G H J K", create: createMarimba },
  { id: "bells", name: "Bells", category: "perc", archetype: "air-keys", scaleLocked: true, polyphony: "poly", color: "#b8e0ff", playHint: "A S D F G H J K", create: createBells },
  { id: "kalimba", name: "Kalimba", category: "plucked", archetype: "air-keys", scaleLocked: true, polyphony: "poly", color: "#9be564", playHint: "A S D F G H J K", create: createKalimba },
  { id: "harp", name: "Harp", category: "plucked", archetype: "strum", scaleLocked: true, polyphony: "poly", color: "#f4a261", playHint: "A S D F G H J K", create: createHarp },
  { id: "fm-bass", name: "FM bass", category: "bass", archetype: "theremin", scaleLocked: true, polyphony: "mono", color: "#5b8cff", playHint: "A S D F G H J K", create: createFmBass },
  { id: "synth-lead", name: "Synth lead", category: "leads", archetype: "theremin", scaleLocked: true, polyphony: "mono", color: "#ff8fd1", playHint: "A S D F G H J K", create: createSynthLead },
  { id: "strings", name: "String ensemble", category: "strings", archetype: "theremin", scaleLocked: true, polyphony: "poly", color: "#b08cff", playHint: "A S D F G H J K", create: createStrings },
  { id: "synth-brass", name: "Synth brass", category: "wind", archetype: "air-keys", scaleLocked: true, polyphony: "poly", color: "#ffd166", playHint: "A S D F G H J K", create: createSynthBrass },
  { id: "shaker", name: "Shaker", category: "perc", archetype: "shake", scaleLocked: false, polyphony: 1, color: "#c9a0dc", playHint: "shake · Z", create: createShaker },
];

const BY_ID = new Map(INSTRUMENTS.map((d) => [d.id, d]));

export function getInstrumentDef(id: string): InstrumentDef {
  const def = BY_ID.get(id);
  if (!def) throw new Error(`Unknown instrument: ${id}`);
  return def;
}

export function createInstrument(id: string): Instrument {
  return getInstrumentDef(id).create();
}
